/**
 * Analytics Module
 *
 * This file contains the interface to our Analytics modules (actions, reducers, APIs, etc);
 *
 * Initial implementation is with Google Analytics
 * We aim to make this module service-agnostic to increase flexibility and allow us to switch services
 *  without changing too much client code.
 *
 */

import GA from 'react-ga';

import { REMOVE_NOTIFICATION, CLEAR_NOTIFICATIONS } from './actions/notificationActions';
import { UPDATE_USER, CLEAR_USER } from './actions/userActions';
import {
  SET_LAST_TRANSACTION,
  SET_TRANSACTION_DETAILS,
  SET_ORDER_STEP,
  SET_ORDER_ID,
  SET_CARDS,
} from './actions/orderActions';
import { OPEN_NAV, CLOSE_NAV } from './actions/navActions';
import { UPDATE_SETTINGS } from './actions/settingsActions';
import { CLOSE_ALL_MODALS } from './actions/modalActions';
import { callApi, detectCookies } from './utils';

// 'UA-43477468-3'
GA.initialize('UA-43477468-1', {
  debug: process.env.NODE_ENV !== 'production',
  gaOptions: {
    siteSpeedSampleRate: 50,
    cookieDomain: 'none',
  },
});

//Conversion strings
const conversionStrings = {
  PURCHASE: 'DsqQCJGTz7EBEOnL2J8D',
  SIGN_UP: 'x_R6CKGQ8rIBEOnL2J8D',
  LEAD: 'nJKdCKqb5rIBEOnL2J8D',
  PAGE_VIEW: '5OkRCKiQ5rIBEOnL2J8D',
  BASE_CONFIG: 'AW-871769577',
};

//Google Tag Conversions
export const selectConversionString = action => {
  return `${conversionStrings['BASE_CONFIG']}/${conversionStrings[action]}`;
};

//Run facebook action
export const runFacebookAction = (action = '') => window.fbq('track', action);

//Run gtag action
export const runGtagAction = (action = '', values = {}) => {
  let data = {
    send_to: selectConversionString(action),
  };

  if (action === 'PURCHASE') {
    data = { ...data, ...values };
  }

  window.gtag('event', 'conversion', data);
};

// Exported Analytics functions

export const setUserId = id => GA.set({ userId: id });

export const view = path => {
  logData({
    action: 'PAGE_VIEW',
    label: path,
    value: null,
    category: 'PAGE',
  });

  GA.pageview(path);

  runGtagAction('PAGE_VIEW');

  // runFacebookAction('PageView');

  if (path === '/') {
    criteoHome();
  }
};

export const modal = name => GA.modalview(name);

export const event = e => GA.event(e); // typeof e === 'object'

// Log data to our internal backend analytics
let queueLogData = [];
const logData = (data = {}) => {
  queueLogData.push(data);
};

if (detectCookies()) {
  // Check Cookie Support before sending Session Analytics
  setInterval(() => {
    if (queueLogData.length) {
      return callApi('/analytics/log', queueLogData.shift(), 'post').catch(console.error);
    }
    return;
  }, 1000); // Send data to API only after 2 second intervals;
}

export const logNoCookies = phone => {
  callApi('/analytics/nocookies?phone=' + phone).catch(() => {});
  callApi('/analytics/log', { action: 'NO_SESSION' }, 'post').catch(() => {});
};

// Log Redux Actions
export const logAction = (action, label, value, category = 'ACTION') => {
  const data = {
    category,
    action,
    label,
    value,
  };

  logData(data);
  GA.event(data);
};

export const logClick = (action, label, value, category = 'CLICK') =>
  logAction(action, label, value, category);

export const logPhoneClick = () => logClick('CLICK', 'SUPPORT_PHONE');

export const logGoal = amount => logAction('PAYMENT_GOAL', null, amount, 'GOAL_EVENT');

// Log Exceptions
export const logError = (description, fatal = false) => {
  try {
    description =
      typeof description === 'object'
        ? JSON.stringify(description)
        : description
        ? description
        : 'Unknown Error';
    GA.exception({
      description,
      fatal,
    });
    // Log errors as actions so they show up on session timeline
    logAction(description, 'ERROR EVENT', 0, 'ERROR');
  } catch (err) {}
};

export const timer = (category, variable, value, label = 'Timer') => {
  GA.timing({
    category,
    variable,
    value,
    label,
  });

  logAction(category, variable, value, 'TIMING');
};

// Analytics redux middleware
const ignoredActionTypes = [
  'persist/PERSIST',
  'persist/REHYDRATE',
  REMOVE_NOTIFICATION,
  CLEAR_NOTIFICATIONS,
  SET_LAST_TRANSACTION,
  SET_TRANSACTION_DETAILS,
  SET_ORDER_STEP,
  SET_ORDER_ID,
  SET_CARDS,
  UPDATE_USER,
  UPDATE_SETTINGS,
  CLEAR_USER,
  OPEN_NAV,
  CLOSE_NAV,
  CLOSE_ALL_MODALS,
]; // List of redux action types to ignore

export const analyticsMiddleware = store => next => action => {
  if (!ignoredActionTypes.includes(action.type)) {
    if (action.level === 'error') {
      logError(action.message);
    } else if (action.level === 'success') {
      logAction(action.message);
    } else {
      logAction(action.type);
    }
  }
  return next(action);
};

// Criteo
// Initialize
window.criteo_q = window.criteo_q || [];
window.criteo_q.push(
  { event: 'setAccount', account: 63494 }, // You should never update this line
  {
    event: 'setSiteType',
    type: /iPad/.test(navigator.userAgent)
      ? 't'
      : /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Silk/.test(navigator.userAgent)
      ? 'm'
      : 'd',
  }
);

export const criteoHome = () => window.criteo_q.push({ event: 'viewHome' });
export const criteoEmail = email =>
  window.criteo_q.push(
    { event: 'setEmail', email } // Can be an empty string
  );
export const criteoProduct = id => window.criteo_q.push({ event: 'viewItem', item: id });
export const criteoSales = ({ ref, amount, disco }) =>
  window.criteo_q.push({
    event: 'trackTransaction',
    id: ref,
    item: [{ id: disco, price: amount, quantity: 1 }],
  });

export default {
  GA,
  setUserId,
  view,
  modal,
  event,
  logAction,
  logClick,
  logGoal,
  logError,
  timer,
  analyticsMiddleware,
  logNoCookies,
  logPhoneClick,
  //
  criteoEmail,
  criteoProduct,
  criteoSales,
  runGtagAction,
  runFacebookAction,
};



// WEBPACK FOOTER //
// ./src/analytics.js